import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAmplitudeTracking } from '@hooks/useAmplitudeTracking';
import { ANALYTICS_EVENTS } from '@lib/analytics-events';
import {
  captureAttribution,
  getAppSharePlatform,
  getAttribution,
  getQrCodeId,
  isAppShareTraffic,
  isQrTraffic,
  markAppShareCapturedThisSession,
  markQrScanCapturedThisSession,
  markReferralCapturedThisSession,
  wasAppShareCapturedThisSession,
  wasQrScanCapturedThisSession,
  wasReferralCapturedThisSession,
} from '@lib/analytics/session';
import { parseQrLanding } from '@lib/analytics/qrLanding';

/**
 * Which QR builder a scan came from, from the `qr_` source suffix. 'legacy' covers
 * printed/external codes predating the two builders (the `qr_<id>` row in the source
 * taxonomy) and the UTM-only arm of `isQrTraffic`, which yields no id at all.
 */
const getQrKind = (qrCodeId: string | null): 'event' | 'invite' | 'legacy' => {
  if (qrCodeId === 'event') return 'event';
  if (qrCodeId === 'invite') return 'invite';
  return 'legacy';
};

export const AnalyticsBootstrapper = (): null => {
  const location = useLocation();
  const { trackEvent } = useAmplitudeTracking();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Persist any attribution params early (before other events fire)
    captureAttribution(window.location.href);
    const attribution = getAttribution();

    const landingPath = `${location.pathname}${location.search}${location.hash}`;

    // Check for QR traffic: utm_source=qr, utm_medium=qr, utm_campaign=qr, or source=qr_[id]
    if (isQrTraffic(attribution) && !wasQrScanCapturedThisSession()) {
      const qrCodeId = getQrCodeId(attribution);
      // `qr_code_id` is `source` minus the `qr_` prefix, so for the two links we build
      // it is the constant 'event' or 'invite' and identifies no artifact. These three
      // are what make a scan joinable to the `qr_code_shown` that produced it. All
      // additive, all derived from `landing_path`/`source` already on this event, so
      // nothing new goes on the wire, and `qr_code_id` stays untouched so no existing
      // chart moves.
      const { entityType, entitySlug } = parseQrLanding(location.pathname);
      trackEvent(ANALYTICS_EVENTS.qr_code_scanned, {
        landing_path: landingPath,
        ref: attribution.ref ?? null,
        qr_code_id: qrCodeId,
        source: attribution.source ?? null,
        qr_kind: getQrKind(qrCodeId),
        entity_type: entityType,
        entity_slug: entitySlug,
      });
      markQrScanCapturedThisSession();
    }

    if (attribution.ref && !wasReferralCapturedThisSession()) {
      trackEvent(ANALYTICS_EVENTS.referral_link_clicked, {
        referral_code: attribution.ref,
        landing_path: landingPath,
      });
      markReferralCapturedThisSession();
    }

    // Track arrivals via shared links (source=app_share or app_share_<platform>)
    // AIDEV-NOTE: Distinct from QR/referral - tracks K-factor (viral spread from in-app sharing)
    // Now includes platform for A/B testing different share channels
    if (isAppShareTraffic(attribution) && !wasAppShareCapturedThisSession()) {
      const platform = getAppSharePlatform(attribution);
      trackEvent(ANALYTICS_EVENTS.shared_link_opened, {
        landing_path: landingPath,
        ref: attribution.ref ?? null,
        source: attribution.source ?? null, // Full source for filtering (app_share_whatsapp, etc.)
        platform: platform, // Extracted platform (whatsapp, linkedin, etc.) or null
        ref_user: attribution.ref_user ?? null, // Who shared (viral loop)
        ref_event: attribution.ref_event ?? null, // What event was shared
        ref_question: attribution.ref_question ?? null, // What question was shared
        ref_profile: attribution.ref_profile ?? null, // What profile was shared
      });
      markAppShareCapturedThisSession();
    }
  }, [location.hash, location.pathname, location.search, trackEvent]);

  return null;
};
